import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Warehouse, LineChart } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="flex min-h-screen bg-slate-950">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-slate-900 p-12 lg:flex">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #6366f1 0, transparent 40%), radial-gradient(circle at 80% 70%, #0ea5e9 0, transparent 40%)" }}
        />
        <div className="relative flex items-center">
          <img src="/logo-ascendra.png" alt="SPH Ascendra" className="h-16 w-auto object-contain" data-testid="login-logo" />
        </div>
        <div className="relative space-y-6">
          <h2 className="font-display text-4xl font-bold leading-tight text-white">
            Satu sistem untuk<br />seluruh operasi bisnis.
          </h2>
          <p className="max-w-md text-sm text-slate-400">
            Kelola pesanan 10 toko online, stok barang jadi terpusat, konveksi, dan digital printing dalam satu platform enterprise.
          </p>
          <div className="grid grid-cols-1 gap-3 pt-4">
            {[
              { icon: Warehouse, t: "Stok Terpusat", d: "Satu pool stok barang jadi untuk semua toko" },
              { icon: LineChart, t: "Analitik Real-time", d: "Penjualan, iklan & performa per toko" },
              { icon: ShieldCheck, t: "Kontrol Akses", d: "RBAC berbasis role & permission" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-800/40 p-3.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500/15 text-indigo-300">
                  <f.icon className="h-4.5 w-4.5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{f.t}</p>
                  <p className="text-xs text-slate-500">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-600">© 2026 SPH Ascendra. Internal use only.</p>
      </div>

      {/* Right form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 lg:hidden flex items-center">
            <img src="/logo-ascendra.png" alt="SPH Ascendra" className="h-12 w-auto object-contain" />
          </div>
          <h1 className="font-display text-2xl font-bold text-white">Selamat datang kembali</h1>
          <p className="mt-1 text-sm text-slate-400">Masuk untuk mengakses dashboard operasional.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="nama@sphascendra.com" data-testid="login-email"
                className="mt-1.5 bg-slate-900 border-slate-700 text-white placeholder:text-slate-600"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Kata Sandi</Label>
              <Input
                id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="••••••••" data-testid="login-password"
                className="mt-1.5 bg-slate-900 border-slate-700 text-white placeholder:text-slate-600"
              />
            </div>
            {error && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white h-11 rounded-lg font-semibold">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Memproses...</> : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
